/**
 * SUPERVISOR-BENCHMARK-QUESTIONS
 * Déclencheur de calcul pour le benchmark de type 'question'. La table de curation
 * (parcourir/scanner/associer/dissocier des variantes question_aid) a été retirée le 2026-09-11 —
 * plus aucun consommateur légitime de question_aid/question_aids ailleurs dans le produit
 * (dashboard, heatmap, panneau éditeur sont tous passés sur bank_id, cf KB
 * features/bank-questions-vision.md §19-21). Le calcul legacy (question_aid, Seed/Calculer
 * distinct, audit export) a été retiré le même jour — calculateQuestionBenchmarksViaBank() est
 * maintenant le seul calcul, un seul bouton (KB §22).
 *
 * "Ajouter une question à la banque" migré ici depuis SupervisorUnmatchedQuestionsModal
 * (2026-09-14) — utile aussi hors du contexte d'un template précis, en supervisor brut.
 *
 * "Banque de questions" (2026-09-14, KB §36) : liste paginée/cherchable de tout le catalogue,
 * reconstruite depuis une maquette (artefact partagé par Tom) — recréée ici avec nos composants
 * plutôt que copiée telle quelle. L'icône crayon ouvre le panneau de gestion des formulations
 * (voir/dissocier/supprimer) qu'on avait justement gardé de côté dans la modale de l'éditeur pour
 * cet usage précis. L'icône calculatrice lance le calcul de la ligne (bleu = pas encore de
 * calc_benchmarks, gris = déjà calculé, toujours cliquable pour forcer un recalcul).
 *
 * maxHeight (2026-09-15, Tom: "la banque de question prenne la meme hauteur que la liste des
 * comptes") — the route measures the sidebar card's real height and passes it down; only the row
 * list itself scrolls (wrapped below), title/search/ListFields header and the pagination footer
 * stay put. ROWS_CHROME_HEIGHT is a fixed estimate of that header+footer chrome (not measured —
 * same approximate-px-budget approach already used elsewhere on this screen) since it doesn't
 * vary with data; subtracted from the real measured maxHeight rather than a flat pixel count, so
 * this keeps working once maxHeight itself grows on a bigger screen ("adapte ca en fonction d'un
 * plus grand écran, responsive").
 *
 * pageSize (same request) — route-derived from viewport height instead of a flat constant,
 * shared with SupervisorAccountsWithoutSectorWidget's own row budget so both lists grow together.
 */

import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "@/core/root-reducer"
import { STATUS_LOADED, STATUS_SEARCHING } from "@/redux/_status.types"
import BankQuestionManageModal from "@/features/benchmark/entities/question/ui/modals/BankQuestionManageModal"
import BankQuestionTagChips from "@/features/benchmark/entities/question/ui/BankQuestionTagChips"
import BusyOverlay from "@/features/benchmark/entities/question/ui/BusyOverlay"
import CreateBankQuestionModal from "@/features/benchmark/entities/question/ui/modals/CreateBankQuestionModal"
import Button from "@/components/button"
import Card from "@/components/card"
import Checkbox from "@/components/checkbox"
import Chip from "@/components/chip"
import ListContainer from "@/components/list-container"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import ListFields from "@/components/list-fields"
import ListButton from "@/components/list-button"
import Tooltip from "@/components/tooltip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCalculator,
  faPlus,
  faPen,
  faCheck,
  faCodeMerge,
  faBars,
  faSort,
  faArrowUpShortWide,
  faArrowDownWideShort
} from "@fortawesome/free-solid-svg-icons"
import {
  listBankQuestions_AsSupervisor,
  mergeBankQuestions_AsSupervisor,
  calculateOneBankQuestion_AsSupervisor,
  BankQuestionListRow,
  BankListSort
} from "@/features/benchmark/entities/question/model/bankQuestion.thunk"

const ROWS_CHROME_HEIGHT = 190 //title+search header + ListFields header + pagination footer, approx

interface Props {
  refreshSignal?: number //Bumped by the route after a page-level calculate completes
  maxHeight?: number //Caps the card's total height to match the sidebar's (see doc comment above)
  pageSize: number //Rows per page, route-derived/responsive (see doc comment above)
}

export default function SupervisorBenchmarkQuestionsWidget(props: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [showCreateModal, setShowCreateModal] = useState(false)

  //"Banque de questions" list — offset (not page number) to match ListContainer's own convention
  //(onSetPage hands back index*countLimit)
  const [search, setSearch] = useState("")
  const [offset, setOffset] = useState(0)
  const [rows, setRows] = useState<BankQuestionListRow[]>([])
  const [total, setTotal] = useState(0)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [managingRow, setManagingRow] = useState<BankQuestionListRow | null>(null)
  //Checkbox-select for merging (KB §37) — ordered, the first one checked is the target that
  //absorbs the others ("après on joue avec l'étoile" to then pick the displayed phrasing, Tom).
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [confirmingMerge, setConfirmingMerge] = useState(false)
  //Merge + its recalculate + the page-0 reload can take a moment (2026-09-17, found live) — a
  //full overlay blocks the checkboxes/calculator/pencil in the meantime instead of leaving them
  //clickable mid-save.
  const [isMerging, setIsMerging] = useState(false)
  //Per-row calculator button (2026-09-17, Ben) — tracks which single row is mid-calculate so
  //only that row's button shows the "in progress" (blue) state, not the whole list.
  const [calculatingRowId, setCalculatingRowId] = useState<string | null>(null)
  //ListContainer tracks its own "current page" as internal state (list-container.tsx), never
  //synced from our own `offset` — it only moves when its own prev/next/page-number is clicked.
  //Every time WE reset offset to 0 for a reason it doesn't know about (search, filter, pageSize,
  //merge), its page indicator at the bottom keeps showing the old page even once the rows
  //themselves are page 1 (found live, 2026-09-17). Bumping this and passing it as `key` remounts
  //ListContainer, which resets that internal state for free — cheaper than changing a shared
  //component's behavior for every other page that uses it.
  const [listResetKey, setListResetKey] = useState(0)

  //Order of the list: alphabetical by default, or by number of formulations (fewest first = latest additions)
  const [sort, setSort] = useState<BankListSort>("alpha")

  async function loadList(searchValue: string, currentOffset: number, sortValue: BankListSort = sort) {
    setIsLoadingList(true)
    const page = Math.floor(currentOffset / props.pageSize) + 1
    const res = await dispatch(
      listBankQuestions_AsSupervisor({
        search: searchValue,
        page,
        pageSize: props.pageSize,
        sort: sortValue
      })
    ).unwrap()
    setRows(res.rows)
    setTotal(res.total)
    setIsLoadingList(false)
  }

  useEffect(() => {
    loadList(search, offset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset])

  //Refetch after the route's own page-level calculate completes — skip the very first render
  //(refreshSignal starts at 0, same as its initial value, no point re-fetching what the [offset]
  //effect above just fetched already)
  const isFirstRefresh = useRef(true)
  useEffect(() => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false
      return
    }
    loadList(search, offset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshSignal])

  //pageSize changes when the viewport is resized (route's computeRowCount) — re-fetch from the
  //first page since offset was in units of the old pageSize. Same skip-first-render guard as
  //above, mount already fetches via the [offset] effect.
  const isFirstPageSize = useRef(true)
  useEffect(() => {
    if (isFirstPageSize.current) {
      isFirstPageSize.current = false
      return
    }
    setOffset(0)
    setListResetKey((prev) => prev + 1)
    loadList(search, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pageSize])

  //SearchBar already debounces 500ms on its own (search-bar.tsx) before calling onSearch — no
  //extra debounce needed here, just reset to the first page on every new term
  function handleSearch(value: string) {
    // No listResetKey bump here (found live, 2026-09-18) — ListContainer's SearchBar isn't
    // controlled from outside (its typed text is purely internal state, no value/searchText prop
    // fed back in), so remounting it on every debounced keystroke reset it back to empty as the
    // user was still typing. The page-indicator desync this key exists for isn't worth breaking
    // the search input itself over — search results are short enough that landing on a stale
    // page number here is a much smaller problem.
    setSearch(value)
    setOffset(0)
    loadList(value, 0)
  }

  //Alphabetical -> fewest formulations first (latest additions) -> most first -> alphabetical
  function handleSort() {
    const next: BankListSort = sort === "alpha" ? "countAsc" : sort === "countAsc" ? "countDesc" : "alpha"
    setSort(next)
    setOffset(0)
    setListResetKey((prev) => prev + 1)
    loadList(search, 0, next)
  }

  function toggleSelect(bankId: string) {
    setConfirmingMerge(false)
    setSelectedIds((prev) => (prev.includes(bankId) ? prev.filter((id) => id !== bankId) : [...prev, bankId]))
  }

  async function handleMerge() {
    if (!confirmingMerge) {
      setConfirmingMerge(true)
      return
    }

    setIsMerging(true)
    try {
      const [targetId, ...sourceIds] = selectedIds
      const res = await dispatch(mergeBankQuestions_AsSupervisor({ targetId, sourceIds })).unwrap()
      setConfirmingMerge(false)
      if (res.error || !res.success) return // fetchBackend already toasted a real error

      setSelectedIds([])
      // mergeBankQuestions() (backend) reattaches the absorbed rows' dim_questions.bank_id onto
      // the target and deletes their own calc_benchmarks, but never recomputes the target itself.
      // Always recalculate, not just when the target already had a benchmark (found live,
      // 2026-09-18 — same gap as "Associer" had: absorbing another row's accounts is often
      // exactly what pushes a "pas encore de benchmark" target past the 5-account threshold).
      await dispatch(calculateOneBankQuestion_AsSupervisor(targetId)).unwrap()
      // Reset to the first page rather than reloading the current offset (found live, 2026-09-17)
      // — merging several rows at once removes that many rows from the total, and reloading at
      // the same offset can land past the new last page, coming back with an empty `rows` page
      // that looks exactly like "the list didn't refresh" instead of an actually-empty result.
      setOffset(0)
      setListResetKey((prev) => prev + 1)
      await loadList(search, 0)
    } finally {
      setIsMerging(false)
    }
  }

  //The calculator button itself is now the trigger for a row without a benchmark yet (2026-09-17,
  //Ben) — was purely an indicator/coverage-toggle before. Refetches the whole list afterwards so
  //hasBenchmark flips to true and the button settles into its grey "already calculated" state.
  async function handleCalculateOne(bankId: string) {
    setCalculatingRowId(bankId)
    try {
      await dispatch(calculateOneBankQuestion_AsSupervisor(bankId)).unwrap()
      await loadList(search, offset)
    } finally {
      setCalculatingRowId(null)
    }
  }

  //Next to the search bar (ListContainer's own extraActions) — swapped for "Fusionner" once 2+
  //rows are checked, mutually exclusive concerns (KB §37)
  const addQuestionAction =
    selectedIds.length >= 2 ? (
      <Button icon={confirmingMerge ? faCheck : faCodeMerge} isWithBorder isFullWidth onClick={handleMerge}>
        {confirmingMerge
          ? t("benchmark_editor_supervisor_merge_confirm")
          : t("benchmark_editor_supervisor_merge", { count: selectedIds.length })}
      </Button>
    ) : (
      <Button icon={faPlus} isWithBorder isFullWidth onClick={() => setShowCreateModal(true)}>
        {t("benchmark_editor_supervisor_create_standalone")}
      </Button>
    )

  return (
    <>
      {/* position:relative here rather than on Card itself — Card doesn't accept a style prop
          (checked its source), so the busy overlay below anchors to this wrapper instead. */}
      <div style={{ position: "relative" }}>
        {isMerging && <BusyOverlay />}
        <Card isWithoutPadding className="mt-0">
        <ListContainer
          key={listResetKey}
          title={t("benchmark_editor_supervisor_bank_title")}
          onSearch={handleSearch}
          searchPlaceholder={t("utils_search")}
          countLimit={props.pageSize}
          countSearch={total}
          onSetPage={setOffset}
          isFooterActive
          placeholder={t("utils_no_results")}
          status={isLoadingList ? STATUS_SEARCHING : STATUS_LOADED}
          extraActions={addQuestionAction}>
          {/* list-container-header's own bottom padding (20px) plus list-container-fields' own
              top padding (18px) stack into an oversized gap here — pulled up, scoped to this
              screen only (list-container/list-fields are shared components used elsewhere). */}
          <div style={{ marginTop: -20 }}>
            <ListFields>
              <ListCell width={70}>
                <Tooltip text={t("benchmark_editor_supervisor_merge_hint")}>
                  <FontAwesomeIcon icon={faCodeMerge} />
                </Tooltip>
              </ListCell>
              <ListCell width={70}>
                <Tooltip
                  text={
                    sort === "alpha"
                      ? t("benchmark_editor_supervisor_sort_alpha", { defaultValue: "Trier par nombre de formulations" })
                      : sort === "countAsc"
                        ? t("benchmark_editor_supervisor_sort_count_asc", {
                            defaultValue: "Moins de formulations d'abord (les plus récentes). Cliquer pour inverser"
                          })
                        : t("benchmark_editor_supervisor_sort_count_desc", {
                            defaultValue: "Plus de formulations d'abord. Cliquer pour revenir à l'ordre alphabétique"
                          })
                  }
                  usePortal>
                  <div className="_hover" style={{ cursor: "pointer" }} onClick={handleSort}>
                    <FontAwesomeIcon
                      icon={sort === "countAsc" ? faArrowUpShortWide : sort === "countDesc" ? faArrowDownWideShort : faSort}
                    />
                  </div>
                </Tooltip>
              </ListCell>
              <ListCell isFlex text={t("benchmark_editor_supervisor_suggestion_col")} />
              <ListCell width={90} />
            </ListFields>
          </div>

          <div
            style={
              props.maxHeight
                ? { maxHeight: Math.max(props.maxHeight - ROWS_CHROME_HEIGHT, 200), overflowY: "auto" }
                : undefined
            }>
          {rows.map((row) => {
            const selectionIndex = selectedIds.indexOf(row.id)

            return (
              <div key={row.id}>
                {/* ListButton/Checkbox already call event.stopPropagation() in their own click
                    handlers (checked their source, 2026-09-17) — so this row-level onClick never
                    fires when clicking the checkbox/calculator/pencil, no extra wrapping needed. */}
                <ListItem isSmall isEditable className="items-center" onClick={() => setManagingRow(row)}>
                  {/* 90px reserved that same gap on every OTHER row just to fit a label only one
                      row ever shows; the absolute-position attempt at 40px overlapped the text
                      column instead (both found live, 2026-09-14). 70px — enough room for
                      checkbox + "Cible" in normal flow, without going back to the original waste. */}
                  <ListCell width={70} className="items-center">
                    {/* Checkbox's own CSS has `margin: auto` (checkbox-container) — as a direct
                        flex child of this cell that centers it, leaving visible empty space on
                        its left (found live, 2026-09-14). Not a component change: a plain
                        non-flex wrapper div breaks that direct-flex-child relationship, so the
                        auto margin has no leftover main-axis space to distribute. */}
                    <div>
                      <Checkbox active={selectionIndex > -1} onClick={() => toggleSelect(row.id)} />
                    </div>
                    {selectionIndex === 0 && selectedIds.length >= 2 && (
                      <span className="grey-t text-sm" style={{ marginLeft: 4, whiteSpace: "nowrap" }}>
                        {t("benchmark_editor_supervisor_merge_target")}
                      </span>
                    )}
                  </ListCell>
                  <ListCell width={70} className="items-center">
                    <Tooltip text={t("benchmark_editor_supervisor_text_count", { count: row.textsCount ?? 0 })} usePortal>
                      <div className="flex">
                        <Chip icon={faBars} color={row.textsCount === 1 ? "#eb5a46" : undefined}>{row.textsCount ?? 0}</Chip>
                      </div>
                    </Tooltip>
                  </ListCell>
                  {/* Not using ListCell's own `text` prop — its shared CSS (list-cell-title/
                      list-cell-text) forces single-line + ellipsis, which either truncates or,
                      for a long enough question, pushes the icons off-screen entirely rather than
                      actually eliding (found live, 2026-09-14). Full wrap here instead, scoped to
                      this row only. */}
                  <ListCell isFlex>
                    <div className="flex items-center gap-1 flex-wrap" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <span>{row.text}</span>
                      {/* Derived from dim_questions.reverse via bank_id (2026-09-21) — not stored
                          on bank_questions itself, so a freshly created/not-yet-linked row won't
                          show it yet. */}
                      <BankQuestionTagChips isReverse={row.isReverse} scalePoints={row.scalePoints} />
                    </div>
                  </ListCell>
                  <ListCell width={90} className="items-center justify-end gap-1">
                    <ListButton
                      icon={faCalculator}
                      text={t(
                        row.hasBenchmark
                          ? "benchmark_editor_supervisor_has_benchmark"
                          : "benchmark_editor_supervisor_no_benchmark_yet"
                      )}
                      isInactive={row.hasBenchmark}
                      isPrimary={!row.hasBenchmark || calculatingRowId === row.id}
                      // Grey is only a status color, not disabled (ListButton's isInactive is
                      // styling-only, see list-button.tsx) — still clickable to force a recalculate
                      // on an already-calculated row: linking a new variant/phrasing to it doesn't
                      // retroactively backfill dim_questions.bank_id or recompute anything on its
                      // own, syncBankIdFromTexts only runs as part of a calculate, so this is the
                      // only way to pick up the new variant's data.
                      onClick={() => handleCalculateOne(row.id)}
                    />
                    <ListButton
                      icon={faPen}
                      text={t("utils_edit")}
                      isColorActive={managingRow?.id === row.id}
                      onClick={() => setManagingRow(row)}
                    />
                  </ListCell>
                </ListItem>
              </div>
            )
          })}
          </div>
        </ListContainer>
      </Card>
      </div>

      {managingRow && (
        <BankQuestionManageModal
          bankId={managingRow.id}
          title={managingRow.text}
          tags={managingRow.tags}
          isReverse={managingRow.isReverse}
          scalePoints={managingRow.scalePoints}
          onClose={() => setManagingRow(null)}
          onChanged={() => loadList(search, offset)}
        />
      )}

      {showCreateModal && (
        <CreateBankQuestionModal onClose={() => setShowCreateModal(false)} onCreated={() => loadList(search, offset)} />
      )}
    </>
  )
}
